import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {SetFieldsOperation} from '@github-ui/optimistic-connection'
import {useEntityMutation} from '@github-ui/optimistic-connection/useEntityMutation'
import {setParentMutation as setParentQueryId} from '@github-ui/server-persisted-queries'
import type {SetParentMutationMutation} from '@github-ui/server-persisted-queries/types/setParentMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'
import {useRelayEnvironment} from 'react-relay'
import {commitLocalUpdate, type Disposable, type RecordSourceProxy} from 'relay-runtime'

import {escapeOptimisticTitleHTML} from '../utils/helpers'
import {
  type ParentSubIssueUpdates,
  type ParentSubIssueUpdateVariables,
  useParentSubIssueUpdates,
} from './parent-sub-issues-optimistic'
import {type OptimisticParentIssue, setParentMutation} from './set-parent-mutation'

export const SET_PARENT_TANSTACK_MUTATION_FLAG = 'issue_viewer_set_parent_tanstack_mutation'

type ConfirmedParent = NonNullable<
  NonNullable<NonNullable<SetParentMutationMutation['addSubIssue']>['subIssue']>['parent']
>
type OptimisticParentFields = Omit<OptimisticParentIssue, 'repository'> & {
  titleHTML: string
  subIssuesSummary: {total: number; completed: number}
  repository: OptimisticParentIssue['repository'] & {
    name: string
    owner: {login: string}
  }
}
type MutationContext = {
  parentUpdates: ParentSubIssueUpdates
  relayUpdate: Disposable
}
type UseSetParentMutationOptions = ParentSubIssueUpdateVariables & {
  owner: string
  repo: string
  onError?: (error: Error) => void
  onCompleted?: () => void
}

function repositoryDisplayFields(nameWithOwner: string): {name: string; owner: {login: string}} {
  const [owner = '', name = ''] = nameWithOwner.split('/')
  return {name, owner: {login: owner}}
}

function optimisticParentFields(parent: OptimisticParentIssue): OptimisticParentFields {
  return {
    ...parent,
    titleHTML: escapeOptimisticTitleHTML(parent.title),
    subIssuesSummary: {total: 0, completed: 0},
    repository: {...parent.repository, ...repositoryDisplayFields(parent.repository.nameWithOwner)},
  }
}

function parentFromOperation(operation: SetFieldsOperation): OptimisticParentFields {
  return operation.fields.parent as OptimisticParentFields
}

function optimisticOperation(issueId: string, parent: OptimisticParentIssue): SetFieldsOperation {
  return {type: 'setFields', id: issueId, fields: {parent: optimisticParentFields(parent)}}
}

function confirmedOperation(
  subIssueId: string,
  parent: ConfirmedParent,
  optimisticParent: OptimisticParentFields,
): SetFieldsOperation {
  return {
    type: 'setFields',
    id: subIssueId,
    fields: {
      parent: {
        ...parent,
        title: optimisticParent.title,
        repository: {...parent.repository, ...repositoryDisplayFields(parent.repository.nameWithOwner)},
      },
    },
  }
}

function writeRelayParent(
  store: RecordSourceProxy,
  subIssueId: string,
  parent: OptimisticParentFields | ConfirmedParent,
  overwriteSummary: boolean,
): void {
  const subIssueRecord = store.get(subIssueId)
  if (!subIssueRecord) return

  const parentRecord = store.get(parent.id) ?? store.create(parent.id, 'Issue')
  parentRecord.setValue(parent.id, 'id')
  for (const field of ['titleHTML', 'url', 'number', 'state', 'stateReason'] as const) {
    parentRecord.setValue(parent[field], field)
  }
  if ('title' in parent) parentRecord.setValue(parent.title, 'title')

  const repository = parent.repository
  const repositoryRecord = store.get(repository.id) ?? store.create(repository.id, 'Repository')
  repositoryRecord.setValue(repository.id, 'id')
  repositoryRecord.setValue(repository.nameWithOwner, 'nameWithOwner')
  parentRecord.setLinkedRecord(repositoryRecord, 'repository')

  let summaryRecord = parentRecord.getLinkedRecord('subIssuesSummary')
  const shouldWriteSummary = !summaryRecord || overwriteSummary
  if (!summaryRecord) {
    summaryRecord = store.create(`${parent.id}:subIssuesSummary`, 'SubIssuesSummary')
    parentRecord.setLinkedRecord(summaryRecord, 'subIssuesSummary')
  }
  if (shouldWriteSummary) {
    summaryRecord.setValue(parent.subIssuesSummary.total, 'total')
    summaryRecord.setValue(parent.subIssuesSummary.completed, 'completed')
  }

  subIssueRecord.setLinkedRecord(parentRecord, 'parent')
}

async function setParentRequest({
  owner,
  repo,
  issueNumber,
  subIssueId,
  parentIssueId,
}: {
  owner: string
  repo: string
  issueNumber: number
  subIssueId: string
  parentIssueId: string
}): Promise<SetParentMutationMutation> {
  let response: Response
  try {
    const query = new URLSearchParams({queryId: setParentQueryId})
    response = await reactFetchJSON(
      `/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/issues/${issueNumber}/parent.json?${query}`,
      {method: 'PUT', body: {issueId: parentIssueId}},
    )
  } catch {
    throw new Error('Failed to set parent')
  }

  let result: {data?: SetParentMutationMutation | null; errors?: ReadonlyArray<{message?: string}> | null} | undefined
  try {
    result = (await response.json()) as typeof result
  } catch {
    result = undefined
  }

  const hasErrors = Boolean(result?.errors?.length)
  const error = result?.errors?.find(item => item.message)?.message
  const data = result?.data
  const subIssue = data?.addSubIssue?.subIssue
  if (!response.ok || hasErrors || !data || subIssue?.id !== subIssueId || subIssue.parent?.id !== parentIssueId) {
    throw new Error(error ?? 'Failed to set parent')
  }
  return data
}

export function useSetParentMutation({
  issueId,
  issueTitle,
  issueNumber,
  issueDatabaseId,
  issueState,
  nameWithOwner,
  currentParentId,
  owner,
  repo,
  onError,
  onCompleted,
}: UseSetParentMutationOptions) {
  const environment = useRelayEnvironment()
  const updateParents = useParentSubIssueUpdates({
    issueId,
    issueTitle,
    issueNumber,
    issueDatabaseId,
    issueState,
    nameWithOwner,
    currentParentId,
  })
  const {mutate} = useEntityMutation<SetParentMutationMutation, Error, MutationContext>({
    scope: {id: `set-parent:${issueId}`},
    mutationFn: operation =>
      setParentRequest({
        owner,
        repo,
        issueNumber,
        subIssueId: issueId,
        parentIssueId: parentFromOperation(operation).id,
      }),
    onMutate: ({operation}) => {
      const parent = parentFromOperation(operation)
      return {
        parentUpdates: updateParents(parent.id),
        relayUpdate: environment.applyUpdate({
          storeUpdater: store => writeRelayParent(store, operation.id, parent, false),
        }),
      }
    },
    getConfirmedOperation: (data, operation) => {
      const subIssue = data.addSubIssue?.subIssue
      return subIssue?.parent
        ? confirmedOperation(subIssue.id, subIssue.parent, parentFromOperation(operation))
        : undefined
    },
    onSuccess: async (data, _variables, context) => {
      const subIssue = data.addSubIssue?.subIssue
      const parent = subIssue?.parent
      try {
        if (subIssue && parent) {
          commitLocalUpdate(environment, store => writeRelayParent(store, subIssue.id, parent, true))
          await context?.parentUpdates.confirm()
        }
        onCompleted?.()
      } finally {
        context?.relayUpdate.dispose()
      }
    },
    onError: async (error, _variables, context) => {
      if (context?.parentUpdates) await context.parentUpdates.rollback(error)
      context?.relayUpdate.dispose()
      onError?.(error)
    },
  })

  return useCallback(
    (parent: OptimisticParentIssue) => {
      if (!isFeatureEnabled(SET_PARENT_TANSTACK_MUTATION_FLAG)) {
        setParentMutation({
          environment,
          input: {subIssueId: issueId, issueId: parent.id, replaceParent: true},
          optimisticParentIssue: parent,
          onError,
          onCompleted: () => onCompleted?.(),
        })
        return
      }
      mutate({operation: optimisticOperation(issueId, parent)})
    },
    [environment, issueId, mutate, onCompleted, onError],
  )
}
